import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PercentageField } from '~/components/GenericForm/PercentageField';
import { CustomDataDialogComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/CustomDataDialogComponent';
import {
  UseAdditionalCostCentersProps,
  useCostCentersList,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/useCostCentersList';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/useStyles';
import { useMenuButton } from '~/hooks/useMenu';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

type PropsType = UseAdditionalCostCentersProps;

export const CostCentersListComponent = (props: PropsType) => {
  const {
    isLoading,
    control,
    index,
    fieldArray: { fields, remove },
    costCenterOptions,
    isCostCenterOptionDisabled,
    disabledCostCenterOptions,
    onAddCostCenterRow,
    getCostCenterName,
    recalculateTotal,
    customFields,
    onListUpdate,
  } = useCostCentersList(props);

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const addCostCenterMenu = useMenuButton();

  const [selectedCustomDataDialog, setSelectedCustomDataDialog] = useState<
    null | number
  >(null);

  const hasAdditionalCostCenters = fields.length > 1;

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box>
      <Table size={'small'}>
        <TableBody>
          {fields.map((field, ccIndex) => {
            const costCenterName = getCostCenterName(field.costCenterId);

            return (
              <TableRow key={field.id}>
                <TableCell>
                  {costCenterName}{' '}
                  {field.isDefault && (
                    <Box component={'span'} sx={sxStyles.defaultIndicator}>
                      {`(${t('laDefault')})`}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={sxStyles.percentageCell}>
                  <Controller
                    control={control}
                    name={`schedule.${index}.empPosition.costCenterDetails.${ccIndex}.percent`}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => {
                      return (
                        <PercentageField
                          value={value ?? ''}
                          onChange={(event) => {
                            onChange(event);
                            recalculateTotal();
                            onListUpdate();
                          }}
                          error={!!error}
                          helperText={error?.message}
                          size={'small'}
                          fullWidth={true}
                          data-testid="cost-center-percentage-input"
                        />
                      );
                    }}
                  />
                </TableCell>
                <TableCell sx={sxStyles.customDataCell}>
                  <Button
                    size={'small'}
                    onClick={() =>
                      setSelectedCustomDataDialog(field.costCenterId)
                    }
                    sx={sxStyles.customDataButton}
                    data-testid="cost-center-custom-data-button"
                  >
                    {t('laCustomData')}
                  </Button>
                </TableCell>
                {hasAdditionalCostCenters && (
                  <TableCell sx={sxStyles.deleteCell}>
                    {!field.isDefault && (
                      <IconButton
                        onClick={() => {
                          remove(ccIndex);
                          recalculateTotal();
                          onListUpdate();
                        }}
                        title={t('delete')}
                        data-testid="cost-center-delete-button"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {costCenterOptions.length > disabledCostCenterOptions.length && (
            <TableRow>
              <TableCell colSpan={hasAdditionalCostCenters ? 4 : 3}>
                <Button
                  onClick={addCostCenterMenu.open}
                  startIcon={<Add />}
                  size={'small'}
                  fullWidth={true}
                  data-testid="cost-center-add-button"
                >
                  {t('add')}
                </Button>
                <Menu
                  anchorEl={addCostCenterMenu.anchorEl}
                  open={addCostCenterMenu.isOpen}
                  onClose={() => {
                    addCostCenterMenu.close();
                  }}
                  data-testid="cost-center-add-menu"
                >
                  <ListSubheader>
                    {t('fEmpAdditionalCostCenters')}
                  </ListSubheader>
                  {costCenterOptions.map((option, index) => {
                    return (
                      <MenuItem
                        key={`add-cost-center-menu-option-${index}`}
                        disabled={isCostCenterOptionDisabled(
                          option.fCostCenterId,
                        )}
                        onClick={() => {
                          onAddCostCenterRow(option);
                          addCostCenterMenu.close();
                        }}
                        data-testid={`add-cost-center-menu-option-${option.fCostCenterId}`}
                      >
                        {`${option.fCostCenterCode} ${option.fCostCenterName}`}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </TableCell>
            </TableRow>
          )}
          <Controller
            control={control}
            name={`schedule.${index}.empPosition.totalCostCenterPercentage`}
            rules={{
              validate: (value) => {
                if (hasAdditionalCostCenters) {
                  return value === 100 || t('errorTotalPercentageNot100');
                }

                return value <= 100 || t('errorTotalPercentageExceeds100');
              },
            }}
            render={({ field: { value }, fieldState: { error } }) => {
              return (
                <>
                  <TableRow>
                    <TableCell sx={sxStyles.totalCell}>{t('total')}</TableCell>
                    <TableCell sx={sxStyles.percentageTotalCell}>
                      {`${value} %`}
                    </TableCell>
                    <TableCell></TableCell>
                    {hasAdditionalCostCenters && <TableCell></TableCell>}
                  </TableRow>
                  {!!error && (
                    <TableRow>
                      <TableCell
                        sx={sxStyles.errorCell}
                        colSpan={hasAdditionalCostCenters ? 4 : 3}
                      >
                        {error?.message}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            }}
          />
        </TableBody>
      </Table>
      {!!selectedCustomDataDialog && (
        <CustomDataDialogComponent
          open
          control={control}
          onClose={() => {
            setSelectedCustomDataDialog(null);
            onListUpdate();
          }}
          customFields={customFields[selectedCustomDataDialog]}
        />
      )}
    </Box>
  );
};
